import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  SidebarMenuComponent,
  SideMenuItem,
} from './sidebar-menu/sidebar-menu.component';
import { LayoutService } from '../../layout.service';
import { AppSvgIconComponent, OverlayService } from '@vallift/ngl';
import { TokenStorageService } from '@core/services/token-storage.service';
import { WorkspaceSwitcherOverlayComponent } from '../workspace-switcher-overlay/workspace-switcher-overlay';
import {
  resolveWorkspaceAvatarClass,
  resolveWorkspaceInitial,
} from '@core/utils/workspace-avatar.util';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, SidebarMenuComponent, AppSvgIconComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit {
  layoutService = inject(LayoutService);
  private readonly overlayService = inject(OverlayService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly router = inject(Router);

  readonly currentWorkspaceName = signal('Workspace');
  readonly workspaceInitial = signal('W');
  readonly workspaceAvatarClass = signal('bg-lime-400');

  mainMenus = signal<SideMenuItem[]>([
    {
      iconPath: 'assets/icons/view-dashboard-outline.svg',
      link: '/dashboard',
      label: 'Dashboard',
      isEnabled: true,
    },
    {
      iconPath: 'assets/icons/view-agenda-outline.svg',
      link: '/forms',
      label: 'Forms',
      isEnabled: true,
    },
    {
      iconPath: 'assets/icons/variable.svg',
      link: '/data-library',
      label: 'Data Library',
      isEnabled: true,
    },
    {
      iconPath: 'assets/icons/account-multiple-plus-outline.svg',
      link: '/people',
      label: 'People',
      isEnabled: true,
    },
  ]);

  workspaceMenus = signal<SideMenuItem[]>([
    {
      iconPath: 'assets/icons/account-multiple-outline.svg',
      link: '/team-members',
      label: 'Members',
      isEnabled: true,
    },
    {
      iconPath: 'assets/icons/widgets-outline.svg',
      link: '/integrations',
      label: 'Integrations',
      isEnabled: true,
    },
    {
      iconPath: 'assets/icons/cog-outline.svg',
      link: '/settings',
      label: 'Settings',
      isEnabled: true,
    },
    {
      iconPath: 'assets/icons/book-open-blank-variant-outline.svg',
      link: '/docs',
      label: 'Docs',
      isEnabled: true,
    },
  ]);

  ngOnInit(): void {
    this.refreshWorkspaceName();
  }

  toggleSidebar() {
    this.layoutService.toggleSidebar();
  }

  onMenuClick(menu: SideMenuItem) {
    this.layoutService.setCurrentMenu(menu);
    this.layoutService.closeMobileNav();
  }

  openWorkspaceSwitcher(): void {
    const currentWorkspace = this.tokenStorage.getWorkspace();

    const dialogRef = this.overlayService.openModal(WorkspaceSwitcherOverlayComponent, {
      data: {
        currentWorkspaceId: currentWorkspace?.id,
      },
      panelClass: ['workspace-switcher-overlay-panel'],
    });

    dialogRef.closed.subscribe((switched) => {
      if (switched) {
        this.refreshWorkspaceName();
        void this.router.navigate(['/dashboard']);
      }
    });
  }

  private refreshWorkspaceName(): void {
    const workspace = this.tokenStorage.getWorkspace();
    const name = workspace?.name ?? 'Workspace';

    this.currentWorkspaceName.set(name);
    this.workspaceInitial.set(resolveWorkspaceInitial(name));
    this.workspaceAvatarClass.set(
      resolveWorkspaceAvatarClass(workspace?.workspaceSlug ?? workspace?.id ?? name),
    );
  }
}
